import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import PmgAssignmentSection from "../product-management-groups/PmgAssignmentSection";
import type { Document, EntityStatus } from "../../types";
import { EntityType, GuideSourceFormat, RagIndexStatus } from "../../types";
import { useAuth } from "../../contexts/AuthContext";
import { isProductionSupportAdmin, isProductManager } from "../../utils/appRoles";
import { readGuideFile, GUIDE_FILE_ACCEPT } from "../../utils/guideFileReader";
import DocumentMarkdownPreview from "./DocumentMarkdownPreview";
import {
  OPERATOR_DOCUMENT_ENTITY_CLASSES,
  OPERATOR_DOCUMENT_CLASS_LABELS,
  isOperatorDocumentEntityClass,
  type OperatorDocumentEntityClass,
} from "../../utils/operatorDocuments";

interface EditDocumentDialogProps {
  document: Document | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: Document) => void;
}

const EditDocumentDialog: React.FC<EditDocumentDialogProps> = ({
  document: doc,
  open,
  onClose,
  onSaved,
}) => {
  const { user } = useAuth();
  const roles = user?.roles ?? [];
  const lockOperatorTypes =
    isProductionSupportAdmin(roles) && !isProductManager(roles);
  const [draft, setDraft] = useState<Document | null>(doc);
  const [saving, setSaving] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [importing, setImporting] = useState(false);
  const [pmgAccessDenied, setPmgAccessDenied] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const readOnly = pmgAccessDenied;

  useEffect(() => {
    setDraft(doc);
    setPmgAccessDenied(false);
    setImportError(null);
    setTab(0);
  }, [doc]);

  const entityClassValue = isOperatorDocumentEntityClass(draft?.entityClass)
    ? draft?.entityClass
    : lockOperatorTypes
      ? "sop"
      : (draft?.entityClass ?? "");

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    setImportError(null);
    try {
      const result = await readGuideFile(file);
      setDraft((prev) =>
        prev
          ? {
              ...prev,
              content: result.text,
              sourceFileName: result.fileName,
              sourceFormat: result.sourceFormat,
            }
          : prev,
      );
      setTab(0);
    } catch (err: unknown) {
      setImportError(err instanceof Error ? err.message : "Failed to import file");
    } finally {
      setImporting(false);
    }
  };

  const handleSave = async () => {
    if (!draft?.uuid) return;
    const entityClass = entityClassValue || draft.entityClass;
    if (!entityClass) {
      setImportError("Document type is required");
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateDocument(draft.uuid, {
        name: draft.name,
        description: draft.description ?? undefined,
        status: draft.status,
        entityClass,
        content: draft.content ?? undefined,
        sourceFormat: draft.sourceFormat ?? GuideSourceFormat.MARKDOWN,
        sourceFileName: draft.sourceFileName ?? undefined,
        expirationDate: draft.expirationDate || undefined,
        clearExpiration: !draft.expirationDate,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Document</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Tenant ID"
          margin="normal"
          value={draft?.tenantId ?? ""}
          disabled
        />
        <TextField
          fullWidth
          label="Name"
          margin="normal"
          value={draft?.name ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, name: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          value={draft?.description ?? ""}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, description: e.target.value } : prev,
            )
          }
        />
        <TextField
          select
          fullWidth
          required
          label="Document type"
          margin="normal"
          value={entityClassValue}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? { ...prev, entityClass: e.target.value as OperatorDocumentEntityClass }
                : prev,
            )
          }
        >
          {OPERATOR_DOCUMENT_ENTITY_CLASSES.map((id) => (
            <MenuItem key={id} value={id}>
              {OPERATOR_DOCUMENT_CLASS_LABELS[id]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          label="Status"
          margin="normal"
          value={draft?.status ?? "INACTIVE"}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, status: e.target.value as EntityStatus } : prev,
            )
          }
        >
          <MenuItem value="DRAFT">DRAFT</MenuItem>
          <MenuItem value="ACTIVE">ACTIVE</MenuItem>
          <MenuItem value="INACTIVE">INACTIVE</MenuItem>
        </TextField>
        <TextField
          fullWidth
          label="Expiration"
          type="datetime-local"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={toDateTimeLocal(draft?.expirationDate)}
          disabled={readOnly}
          onChange={(e) =>
            setDraft((prev) =>
              prev
                ? { ...prev, expirationDate: fromDateTimeLocal(e.target.value) }
                : prev,
            )
          }
        />
        {draft?.ragIndexStatus && (
          <Alert severity={draft.ragIndexStatus === RagIndexStatus.FAILED ? "warning" : "info"} sx={{ mt: 1 }}>
            Index status: {draft.ragIndexStatus}
          </Alert>
        )}
        {draft?.ragIndexStatus === RagIndexStatus.FAILED && (
          <Button
            variant="outlined"
            sx={{ mt: 1, textTransform: "none" }}
            disabled={readOnly || retrying}
            onClick={async () => {
              if (!draft?.uuid) return;
              setRetrying(true);
              try {
                const updated = await apiService.retryDocumentRag(draft.uuid);
                onSaved(updated);
                setDraft(updated);
              } catch (err: unknown) {
                alert(err instanceof Error ? err.message : "Failed to retry indexing");
              } finally {
                setRetrying(false);
              }
            }}
          >
            {retrying ? "Retrying…" : "Retry index"}
          </Button>
        )}
        <Tabs
          value={tab}
          onChange={(_, value: number) => setTab(value)}
          sx={{ mt: 1, mb: 1, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Preview" />
          <Tab label="Markdown" />
        </Tabs>
        {tab === 0 ? (
          <DocumentMarkdownPreview content={draft?.content} />
        ) : (
          <>
            <Button
              component="label"
              variant="outlined"
              sx={{ mt: 1, textTransform: "none" }}
              disabled={readOnly || importing}
            >
              {importing ? "Converting…" : "Import file"}
              <input type="file" hidden accept={GUIDE_FILE_ACCEPT} onChange={handleImport} />
            </Button>
            <TextField
              fullWidth
              label="Content (Markdown)"
              margin="normal"
              value={draft?.content ?? ""}
              disabled={readOnly}
              multiline
              minRows={8}
              onChange={(e) =>
                setDraft((prev) =>
                  prev ? { ...prev, content: e.target.value } : prev,
                )
              }
            />
          </>
        )}
        {importError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {importError}
          </Alert>
        )}
        {draft?.uuid && (
          <PmgAssignmentSection
            entityUuid={draft.uuid}
            entityType={EntityType.DOCUMENT}
            onAccessDenied={setPmgAccessDenied}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || pmgAccessDenied || importing}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toDateTimeLocal(value?: string | null): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value.length >= 16 ? value.slice(0, 16) : "";
  }
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDateTimeLocal(value: string): string | null {
  return value ? `${value}:00` : null;
}

export default EditDocumentDialog;
